import type { UserType } from "@/types/entities/users";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Checkbox,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@elaniintc/goes-design-system";
import { useRegistryForm } from "../contexts/RegistryFormContext";
import { useCallback, useMemo, useRef, useState } from "react";
import type { EntityRoleType } from "@/schemas/digital-files";
import { UserApplicantSearchSelector } from "./applicant-search-selector";
import { getAssociate, getRoleIcon } from "../../utils";
import { OWNER_ROLE_ID } from "../../types";

export const UsersList = () => {
  const { users, rolesList, setUsers } = useRegistryForm();

  const handleAddUser = useCallback(
    (user: UserType) => {
      const associate = getAssociate(user);
      setUsers((prev) => [...prev, associate]);
      setExpandedUsers((prev) => new Set(prev).add(associate.applicant_id));
    },
    [setUsers],
  );

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setUsers((prev) => prev.filter((u) => u.applicant_id !== userId));
    },
    [setUsers],
  );

  const toggleRole = (role: EntityRoleType, userId: string) => {
    const updateUser = (user: (typeof users)[number]) => {
      const currentRoleIds =
        user.role_ids || (user.role_id ? [user.role_id] : []);
      const hasRole = currentRoleIds.includes(role.id);
      const newRoleIds = hasRole
        ? currentRoleIds.filter((id) => id !== role.id)
        : [...currentRoleIds, role.id];
      const isOwner = newRoleIds.includes(OWNER_ROLE_ID);
      const newRoles = (rolesList || [])
        .filter((r) => newRoleIds.includes(r.id))
        .map((r) => ({ id: r.id, name: r.name }));
      return {
        ...user,
        role_ids: newRoleIds,
        roles: newRoles,
        is_owner: isOwner,
        role_id: newRoleIds[0],
        role: newRoles[0] ?? null,
      };
    };

    setUsers((prev) =>
      prev.map((user) =>
        user.applicant_id === userId ? updateUser(user) : user,
      ),
    );
  };

  const removeRole = (roleId: string, userId: string) => {
    const role = rolesList?.find((r) => r.id === roleId);
    if (role) {
      toggleRole(role, userId);
    }
  };

  const allUserIds = useMemo(
    () => new Set(users.map((u) => u.applicant_id)),
    [users],
  );
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(allUserIds);

  const toggleExpanded = (userId: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  return (
    <>
      <UserApplicantSearchSelector
        label="Usuarios asociados"
        labelClassName="text-size-body text-body-loud"
        selectedUser={users}
        onSelectUser={handleAddUser}
        showCount={users.length > 0}
        count={users.length}
      />
      <div className="flex flex-col gap-2">
        {users.map((user) => {
          const userRoleIds =
            user.role_ids || (user.role_id ? [user.role_id] : []);
          const userRoles = user.roles || (user.role ? [user.role] : []);

          return (
            <div
              key={user.applicant_id}
              className="flex flex-col gap-2.5 p-1.5 rounded-xl border border-neutral-silent bg-neutral-object-accent-silent"
            >
              {/* Top row: avatar + name + email + collapse/delete */}
              <div className="relative flex items-center gap-3 py-2 px-3 rounded-lg border border-neutral-quiet bg-neutral-object">
                <button
                  type="button"
                  className="absolute inset-0 rounded-lg cursor-pointer hover:bg-neutral-object-accent-quiet"
                  onClick={() => toggleExpanded(user.applicant_id)}
                  aria-label={`Expandir ${user.applicant?.first_name || ""} ${user.applicant?.last_name || ""}`}
                />
                <Avatar size="xs" className="pointer-events-none">
                  <AvatarFallback color={user?.is_owner ? "success" : "action"}>
                    {user.applicant?.first_name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex items-center gap-2 flex-auto min-w-0 pointer-events-none">
                  <span className="text-body-loud font-semibold truncate shrink-0 max-w-[40%]">
                    {user.applicant?.first_name || ""}{" "}
                    {user.applicant?.last_name || ""}
                  </span>
                  <span className="text-body-quiet truncate shrink min-w-0">
                    {user.applicant?.email}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Eliminar usuario"
                  onClick={() => handleRemoveUser(user.applicant_id)}
                  className="relative"
                >
                  <Icon type="fas" name="times" className="text-body" />
                </Button>
              </div>

              {/* Bottom row: role badges + add role + collapse toggle */}
              {expandedUsers.has(user.applicant_id) && (
                <div className="flex items-center gap-1.5 flex-wrap px-1">
                  {userRoles.map((role) => {
                    const isOwnerRole = role.id === OWNER_ROLE_ID;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => {
                          if (!role.id) return;
                          removeRole(role.id, user.applicant_id);
                        }}
                        className={cn(
                          "flex items-center justify-center gap-1 min-w-6 h-6 px-1.5 py-0 rounded-lg text-xs font-medium cursor-pointer",
                          isOwnerRole
                            ? "border-[0.5px] border-contextual-success bg-surface-success-accent-quiet text-success"
                            : "border-[0.5px] border-contextual-action bg-surface-action-accent-quiet text-action",
                        )}
                      >
                        {role.name}
                        <Icon type="fas" name="times" className="text-2xs" />
                      </button>
                    );
                  })}
                  {rolesList && rolesList.length > 0 && (
                    <AddRoleButton
                      rolesList={rolesList}
                      selectedRoleIds={userRoleIds}
                      onToggleRole={(role) =>
                        toggleRole(role, user.applicant_id)
                      }
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(user.applicant_id)}
                    className="flex items-center justify-center size-6 rounded-lg border border-neutral bg-neutral-object shadow-neutral-xs cursor-pointer ml-auto"
                    aria-label="Contraer roles"
                  >
                    <Icon
                      type="far"
                      name="angle-up"
                      className="text-body-quiet text-xs"
                    />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

interface AddRoleButtonProps {
  rolesList: EntityRoleType[];
  selectedRoleIds: string[];
  onToggleRole: (role: EntityRoleType) => void;
}

const AddRoleButton = ({
  rolesList,
  selectedRoleIds,
  onToggleRole,
}: AddRoleButtonProps) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant="ghost"
          size="xs"
          onPointerDown={(e) => {
            e.preventDefault();
            setOpen((v) => !v);
          }}
          onClick={(e) => e.preventDefault()}
          className="text-body font-semibold text-sm leading-3xl min-w-7 max-w-180 py-0.5 px-1.5 gap-1 rounded-lg border border-neutral bg-neutral-object shadow-neutral-xs truncate"
        >
          Añadir rol <Icon type="fas" name="plus" className="text-2xs" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="p-2 overflow-hidden shadow-neutral-md min-w-48"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-60 overflow-y-auto">
          {rolesList.map((role, index) => {
            const isSelected = selectedRoleIds.includes(role.id);
            const isOwnerRole = role.id === OWNER_ROLE_ID;
            const hasCustomRoles = rolesList.some(
              (r) => r.id !== OWNER_ROLE_ID,
            );
            return (
              <div key={role.id}>
                <button
                  type="button"
                  data-testid={`role-option-${role.id}`}
                  onClick={() => onToggleRole(role)}
                  className={cn(
                    "flex justify-between items-center w-full p-2 hover:bg-neutral-object-accent-silent rounded-lg cursor-pointer select-none",
                    isSelected && "bg-neutral-object-accent-quiet",
                  )}
                >
                  <div className="flex gap-3 items-center font-normal">
                    <Icon
                      type="far"
                      name={getRoleIcon(role.id)}
                      className="text-body-quiet"
                    />
                    <span className="text-body">{role.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOwnerRole && (
                      <Badge size="sm" shape="pill" variant="success">
                        Propietario
                      </Badge>
                    )}
                    <Checkbox checked={isSelected} readOnly />
                  </div>
                </button>
                {isOwnerRole &&
                  hasCustomRoles &&
                  index < rolesList.length - 1 && (
                    <div className="border-b border-neutral-quiet my-1" />
                  )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
